import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileText, Download, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../../lib/api";

export default function AdminForms() {
  const [data, setData] = useState({ templates: [], responses: [], families: [], annual_keys: [] });
  const [loading, setLoading] = useState(true);
  const [pdfEdits, setPdfEdits] = useState({});
  const [filterFamily, setFilterFamily] = useState("all");
  const [filterExp, setFilterExp] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/forms-dashboard").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const keyName = useMemo(() => Object.fromEntries(data.templates.map((t) => [t.key, t.name])), [data.templates]);

  const toggleRequired = async (t, value) => {
    try {
      await api.put(`/admin/form-templates/${t.id}`, { required: value });
      toast.success(`${t.name} ${value ? "marked required" : "set optional"}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveVersion = async (t) => {
    const pdf_url = pdfEdits[t.id] !== undefined ? pdfEdits[t.id] : t.pdf_url;
    try {
      await api.put(`/admin/form-templates/${t.id}`, { pdf_url, bump_version: true });
      toast.success(`${t.name} updated to a new version. Existing signed records keep their original version.`);
      setPdfEdits((p) => { const n = { ...p }; delete n[t.id]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadDoc = (resp) => {
    const token = localStorage.getItem("hea_token");
    fetch(`${API}/admin/form-responses/${resp.id}/document`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      })
      .catch(() => toast.error("Could not open document."));
  };

  const experiences = useMemo(() => {
    const m = {};
    data.responses.forEach((r) => { if (r.experience_id) m[r.experience_id] = r.experience; });
    return Object.entries(m);
  }, [data.responses]);

  const filteredResponses = data.responses.filter((r) =>
    (filterFamily === "all" || r.family_id === filterFamily) &&
    (filterExp === "all" || r.experience_id === filterExp)
  );

  const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch { return d; } };

  return (
    <AppShell admin>
      <h1 className="font-display text-3xl font-bold text-navy">Forms & Documents</h1>
      <p className="text-slateBrand">Manage required forms, review electronic signatures, and download signed documents for minors securely.</p>

      {/* TEMPLATE CONFIG */}
      <h2 className="mt-8 font-display text-lg font-bold text-navy">Form Configuration</h2>
      <div className="mt-4 space-y-3">
        {data.templates.map((t) => (
          <div key={t.id} data-testid={`admin-form-template-${t.key}`} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brandblue/10"><FileText className="h-5 w-5 text-brandblue" /></div>
                <div>
                  <div className="font-display font-semibold text-navy">{t.name}</div>
                  <div className="text-xs text-slateBrand">{t.category === "event" ? "Event-specific" : "Annual / reusable"} • v{t.version}{t.key === "transportation" ? " • required only when H.E.A. transport is selected" : ""}</div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-navy">
                Required
                <Switch data-testid={`admin-form-required-${t.key}`} checked={t.required !== false} onCheckedChange={(v) => toggleRequired(t, v)} />
              </label>
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Label className="text-xs">Original PDF link (saving creates a new version)</Label>
                <Input data-testid={`admin-form-pdf-${t.key}`} className="mt-1 h-10" value={pdfEdits[t.id] !== undefined ? pdfEdits[t.id] : (t.pdf_url || "")}
                  onChange={(e) => setPdfEdits((p) => ({ ...p, [t.id]: e.target.value }))} placeholder="https://…" />
              </div>
              {t.pdf_url && <a href={t.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex h-10 items-center gap-1 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-brandblue"><ExternalLink className="h-4 w-4" />Open</a>}
              <Button size="sm" data-testid={`admin-form-save-${t.key}`} onClick={() => saveVersion(t)} className="h-10 bg-brandblue font-display font-semibold hover:bg-blue-700">Save new version</Button>
            </div>
          </div>
        ))}
      </div>

      {/* ANNUAL COMPLETION */}
      <h2 className="mt-10 font-display text-lg font-bold text-navy">Annual Form Completion by Family</h2>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slateBrand">
            <th className="p-3">Parent</th><th className="p-3">Students</th>
            {data.annual_keys.map((k) => <th key={k} className="p-3">{keyName[k] || k}</th>)}
          </tr></thead>
          <tbody>
            {data.families.map((f) => (
              <tr key={f.family_id} data-testid={`admin-form-family-${f.family_id}`} className="border-b border-slate-100 last:border-0">
                <td className="p-3 font-semibold text-navy">{f.parent_name}<div className="text-xs font-normal text-slateBrand">{f.parent_email}</div></td>
                <td className="p-3 text-slateBrand">{f.students.join(", ") || "—"}</td>
                {data.annual_keys.map((k) => (
                  <td key={k} className="p-3">
                    {f.annual_signed[k]
                      ? <span className="inline-flex items-center gap-1 text-successBrand"><Check className="h-4 w-4" />{k === "photo_media" && f.media_choice ? (f.media_choice === "authorize" ? "Authorized" : "Declined") : "Signed"}</span>
                      : <span className="inline-flex items-center gap-1 text-alertBrand"><X className="h-4 w-4" />Missing</span>}
                  </td>
                ))}
              </tr>
            ))}
            {data.families.length === 0 && <tr><td colSpan={2 + data.annual_keys.length} className="p-6 text-center text-slateBrand">No families yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* SIGNED DOCUMENTS */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-navy">Signed Documents</h2>
        <div className="flex flex-wrap gap-2">
          <Select value={filterFamily} onValueChange={setFilterFamily}>
            <SelectTrigger data-testid="admin-form-filter-family" className="h-10 w-56"><SelectValue placeholder="All families" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All families</SelectItem>
              {data.families.map((f) => <SelectItem key={f.family_id} value={f.family_id}>{f.parent_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterExp} onValueChange={setFilterExp}>
            <SelectTrigger data-testid="admin-form-filter-exp" className="h-10 w-56"><SelectValue placeholder="All experiences" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All experiences</SelectItem>
              {experiences.map(([id, title]) => <SelectItem key={id} value={id}>{title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slateBrand">
            <th className="p-3">Form</th><th className="p-3">Parent</th><th className="p-3">Student(s)</th><th className="p-3">Experience</th><th className="p-3">Signed</th><th className="p-3">v</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="p-6 text-center text-slateBrand">Loading…</td></tr> :
              filteredResponses.map((r) => (
                <tr key={r.id} data-testid={`admin-form-response-${r.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="p-3 font-semibold text-navy">{r.form_name}{r.media_choice ? <span className="ml-1 text-xs font-normal text-slateBrand">({r.media_choice})</span> : ""}</td>
                  <td className="p-3 text-slateBrand">{r.parent_name}</td>
                  <td className="p-3 text-slateBrand">{r.students.join(", ") || "—"}</td>
                  <td className="p-3 text-slateBrand">{r.experience || "Annual"}</td>
                  <td className="p-3 text-slateBrand">{fmt(r.signed_at)}<div className="text-xs">by {r.signer}</div></td>
                  <td className="p-3 text-slateBrand">v{r.version}</td>
                  <td className="p-3"><Button size="sm" variant="outline" data-testid={`admin-form-download-${r.id}`} onClick={() => downloadDoc(r)}><Download className="mr-1 h-4 w-4" />Download</Button></td>
                </tr>
              ))}
            {!loading && filteredResponses.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slateBrand">No signed documents match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
